import { ECBasicOption } from "echarts/types/dist/shared";
import React, {
    useCallback,
    useMemo,
    useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { loginActions } from "../../pages/LoginPage/reducer";
import { chartUtil } from "../../util/library/chart-util";
import { selectOneSetting } from "../../util/store/selectors";
import { GlobalSetting } from "../../util/varibles/userSetting";

import LoadingBox from "../LoadingBox";

import {
    createSeries,
    createYAxis,
} from "./constants";

import LineChart from "../Chart/LineChart";
import styles from "./style.module.scss";

interface IWeatherData {
    weather: Record<string, any>;
    typical_condition: number;
}

interface IProps {
    weather?: IWeatherData;
    loading?: boolean;
    minInterval?: number;
    height?: number;
    width?: number | string;
}

interface ILegendSelectChangedParams {
    selected: Record<string, boolean>;
}

const DEFAULT_WEATHER: IWeatherData = {
    weather: {},
    typical_condition: 0,
};

const timeFormat: Record<number, string> = {
    10800000: "{HH}:{mm}",
};

const WeatherChart: React.FC<IProps> = React.memo(({
    weather = DEFAULT_WEATHER,
    loading = false,
    minInterval = 86400000,
    height = 350,
    width = "100%",
}) => {
    const dispatch = useDispatch();

    const filter = useSelector(
        selectOneSetting(GlobalSetting.WEATHER),
    );

    const [isEmpty, setIsEmpty] = useState(true);

    /*
     * Convert dữ liệu weather thành series và yAxis.
     */
    const chartData = useMemo(() => {
        const weatherData = weather?.weather ?? {};

        const {
            series,
            yAxis,
        } = createSeries(weatherData);

        return {
            series,
            yAxis,
            isEmpty: series.length === 0,
        };
    }, [weather]);

    /*
     * Đồng bộ trạng thái empty.
     *
     * Có thể dùng trực tiếp chartData.isEmpty, nhưng giữ state nếu những
     * component khác đang phụ thuộc vào cách hoạt động cũ.
     */
    React.useEffect(() => {
        setIsEmpty(chartData.isEmpty);
    }, [chartData.isEmpty]);

    const handleLegendSelectChanged = useCallback((
        params: ILegendSelectChangedParams,
    ) => {
        dispatch(
            loginActions.updateUserSetting([
                {
                    [GlobalSetting.WEATHER]: params.selected,
                },
            ]),
        );
    }, [dispatch]);

    const chartEvents = useMemo(() => ({
        legendselectchanged: handleLegendSelectChanged,
    }), [handleLegendSelectChanged]);

    /*
     * Toàn bộ config của chart được tạo từ WeatherChart.
     * LineChart chỉ chịu trách nhiệm render và quản lý lifecycle.
     */
    const option = useMemo<ECBasicOption>(() => ({
        animation: false,

        tooltip: chartUtil.formatTimeTooltip(),

        legend: {
            bottom: "-5px",
            selected: filter,
            textStyle: {
                color: "--",
            },
        },

        grid: {
            top: "32px",
            right: "40px",
            bottom: "45px",
            left: "16px",
            containLabel: true,
        },

        xAxis: {
            type: "time",
            minInterval,
            ...chartUtil.formatTimeXAxis({
                formatter: timeFormat[minInterval],
            }),
        },

        yAxis: createYAxis(
            filter,
            chartData.yAxis,
        ),

        series: chartData.series,
    }), [
        filter,
        minInterval,
        chartData.series,
        chartData.yAxis,
    ]);

    return (
        <div className="relative w-full">
            <LineChart
                option={option}
                width={width}
                height={height}
                events={chartEvents}
                lazyUpdate
                style={{ opacity: isEmpty ? 0 : 1, }}
            />

            {(isEmpty || loading) && (
                <LoadingBox
                    className="h-3/4"
                    imgEmpty={(
                        <div className={styles["no-weather-icon"]} />
                    )}
                    loading={loading}
                    level="bg"
                    type="custom"
                    message={(
                        <span>
                            Sorry, system can not get weather data
                            <br />
                            for this operation
                        </span>
                    )}
                />
            )}
        </div>
    );
}, (prevProps, nextProps) => (
    prevProps.weather === nextProps.weather
    && prevProps.loading === nextProps.loading
    && prevProps.height === nextProps.height
    && prevProps.width === nextProps.width
    && prevProps.minInterval === nextProps.minInterval
));

WeatherChart.displayName = "WeatherChart";

export default WeatherChart;